import React from "react";
import { Link } from "react-router-dom";
import { Wordmark } from "../../brand/Brand";

/* Shared site footer. Rendered once, globally, so every route ends on the
 * same footing. Links: SPA <Link> for in-app paths, plain <a> for home-page
 * anchors (so they resolve from any page) and external destinations. */
const FOOTER_COLS = [
  {
    title: "Practice",
    links: [
      { label: "MVP & prototyping", href: "/#services" },
      { label: "Web development", href: "/#services" },
      { label: "Mobile (iOS / Android)", href: "/#services" },
      { label: "Design & UX", href: "/#services" },
      { label: "AI integrations", href: "/#services" },
    ],
  },
  {
    title: "Studio",
    links: [
      { label: "Work", href: "/work" },
      { label: "Blog", href: "/blog" },
      { label: "Approach", href: "/#approach" },
      { label: "Contact", href: "/#contact" },
      // Linked site-wide on purpose: it's how crawlers reach every case study
      // and post from any page, which keeps crawl depth at two clicks.
      { label: "Sitemap", href: "/sitemap" },
    ],
  },
  {
    title: "Elsewhere",
    links: [
      {
        label: "App Store",
        href: "https://apps.apple.com/us/developer/jon-michael-narvaez/id1246305819",
        external: true,
      },
      {
        label: "Play Store",
        href: "https://play.google.com/store/apps/developer?id=bboy+creative",
        external: true,
      },
      {
        label: "Instagram",
        href: "https://www.instagram.com/bboycreative.io/",
        external: true,
      },
      {
        label: "LinkedIn",
        href: "https://www.linkedin.com/in/jmcancode/",
        external: true,
      },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Terms of Service", href: "/legal/terms" },
      { label: "Refunds & Chargebacks", href: "/legal/refunds" },
      { label: "Privacy Policy", href: "/legal/privacy" },
      { label: "Cookie Policy", href: "/legal/cookies" },
      { label: "Your Data", href: "/legal/your-data" },
    ],
  },
];

const linkStyle = {
  color: "var(--fw-cream)",
  textDecoration: "none",
  fontSize: 14,
  transition: "color 0.15s",
  width: "fit-content",
};

const onEnter = (e) => (e.currentTarget.style.color = "var(--fw-stone)");
const onLeave = (e) => (e.currentTarget.style.color = "var(--fw-cream)");

const FooterLink = ({ link }) => {
  // Anything not external routes through React Router — including in-page hash
  // links like "/#contact". Going through <Link> (instead of a raw <a>) updates
  // the router location, which is what lets useSmoothScroll drive Lenis to the
  // anchor; a native hash jump does nothing while Lenis owns the scroll.
  const isExternal =
    !!link.external ||
    /^(https?:)?\/\//.test(link.href) ||
    link.href.startsWith("mailto:") ||
    link.href.startsWith("tel:");
  if (!isExternal) {
    return (
      <Link
        to={link.href}
        style={linkStyle}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        {link.label}
      </Link>
    );
  }
  return (
    <a
      href={link.href}
      {...(link.external
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {})}
      style={linkStyle}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {link.label}
    </a>
  );
};

const SiteFooter = () => (
  <footer
    style={{
      background: "var(--fw-ink)",
      color: "var(--fw-cream)",
      padding: "64px 40px 32px",
    }}
  >
    <div style={{ maxWidth: 1440, margin: "0 auto" }}>
      <div
        className="sf-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1fr",
          gap: 48,
          marginBottom: 64,
        }}
      >
        <div>
          <Wordmark size={36} color="var(--fw-cream)" />
          <div
            style={{
              marginTop: 20,
              fontSize: 14,
              lineHeight: 1.6,
              color: "var(--fw-muted-2)",
              maxWidth: 320,
            }}
          >
            A software studio. MVPs, web apps, and mobile, built with the
            operator's lens.
          </div>
          <div
            style={{
              marginTop: 24,
              fontFamily: "var(--fw-mono)",
              fontSize: 11,
              letterSpacing: "1.4px",
              textTransform: "uppercase",
              color: "var(--fw-muted-2)",
            }}
          >
            San Antonio · Remote worldwide
          </div>
        </div>
        {FOOTER_COLS.map((col) => (
          <div key={col.title}>
            <div
              style={{
                fontFamily: "var(--fw-mono)",
                fontSize: 11,
                letterSpacing: "1.4px",
                textTransform: "uppercase",
                color: "var(--fw-muted-2)",
                marginBottom: 16,
              }}
            >
              {col.title}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {col.links.map((l) => (
                <FooterLink key={l.label} link={l} />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div
        className="sf-baseline"
        style={{
          borderTop: "1px solid rgba(139,132,121,0.4)",
          paddingTop: 24,
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
          fontFamily: "var(--fw-mono)",
          fontSize: 10,
          letterSpacing: "1.2px",
          textTransform: "uppercase",
          color: "var(--fw-muted-2)",
        }}
      >
        <span>© 2026 bboy creative — A software studio</span>
        <span>San Antonio, TX · jm@bboycreative.com</span>
      </div>
    </div>

    {/* Self-contained responsive rules so the footer travels with the
        component instead of depending on any one page's stylesheet. */}
    <style>{`
      @media (max-width: 900px) {
        .sf-grid { grid-template-columns: 1fr 1fr !important; }
      }
      @media (max-width: 600px) {
        .sf-grid { grid-template-columns: 1fr !important; }
        .sf-baseline { flex-direction: column; }
      }
      @media (max-width: 600px) {
        footer { padding: 48px 24px 24px !important; }
      }
    `}</style>
  </footer>
);

export default SiteFooter;
